/**
 * JSON-LD Structured Data for SEO
 * Helps search engines and AI bots understand our content
 */

import { SITE_URL, BUSINESS_INFO, SITE_NAME } from "./page-meta";

/**
 * Organization + LocalBusiness schema
 * Used on all pages to establish business identity
 */
export function getOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": ["Organization", "LocalBusiness"],
    "@id": `${SITE_URL}/#organization`,
    name: BUSINESS_INFO.name,
    description: BUSINESS_INFO.description,
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.svg`,
    telephone: BUSINESS_INFO.telephone,
    address: {
      "@type": "PostalAddress",
      addressLocality: BUSINESS_INFO.address.addressLocality,
      addressRegion: BUSINESS_INFO.address.addressRegion,
      addressCountry: BUSINESS_INFO.address.addressCountry,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: BUSINESS_INFO.geo.latitude,
      longitude: BUSINESS_INFO.geo.longitude,
    },
    areaServed: BUSINESS_INFO.areaServed.map((area) => ({
      "@type": "City",
      name: area,
    })),
    priceRange: BUSINESS_INFO.priceRange,
    sameAs: [
      // Add social media links here when available
    ],
  };
}

/**
 * WebApplication schema
 * Used for tool pages (Chat, Code Library, Quantity Survey, etc.)
 */
export function getWebApplicationSchema(
  name: string,
  description: string,
  url: string,
  category: string = "BusinessApplication",
) {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name,
    description,
    url,
    applicationCategory: category,
    operatingSystem: "Web Browser",
    browserRequirements: "Requires JavaScript",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "CAD",
    },
    provider: {
      "@type": "Organization",
      name: BUSINESS_INFO.name,
      url: SITE_URL,
    },
    areaServed: {
      "@type": "AdministrativeArea",
      name: "Alberta, Canada",
    },
  };
}

/**
 * FAQPage schema
 * Used for Chat and Permits pages to show Q&A rich snippets
 */
export function getFAQSchema(
  faqs: Array<{ question: string; answer: string }>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

/**
 * Article/BlogPosting schema
 * Used for blog posts
 */
export function getBlogPostSchema(post: {
  title: string;
  description: string;
  slug: string;
  datePublished: string;
  dateModified?: string;
  authorName: string;
  authorSlug: string;
  image?: string;
  keywords?: string[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.description,
    url: `${SITE_URL}/blog/${post.slug}`,
    datePublished: post.datePublished,
    dateModified: post.dateModified || post.datePublished,
    image: post.image || `${SITE_URL}/og-image.png`,
    author: {
      "@type": "Person",
      name: post.authorName,
      url: `${SITE_URL}/blog?author=${post.authorSlug}`,
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/favicon.svg`,
      },
    },
    keywords: post.keywords?.join(", "),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}/blog/${post.slug}`,
    },
  };
}

/**
 * BreadcrumbList schema
 * Shows breadcrumb navigation in search results
 */
export function getBreadcrumbSchema(
  items: Array<{ name: string; url: string }>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.url}`,
    })),
  };
}

/**
 * SoftwareApplication schema
 * Alternative to WebApplication, better for certain tools
 */
export function getSoftwareApplicationSchema(
  name: string,
  description: string,
  url: string,
) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name,
    description,
    url,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "CAD",
    },
  };
}

/**
 * Pre-built FAQ schemas for specific pages
 */
export const CHAT_PAGE_FAQS = [
  {
    question:
      "What is the minimum stair width for residential buildings in Alberta?",
    answer:
      "860mm (33.9 inches) per NBC(AE) 2023 Section 9.8.4.1. This applies to interior stairs serving a single dwelling unit.",
  },
  {
    question: "What are the egress window requirements in Calgary?",
    answer:
      "Egress windows must have a minimum openable area of 0.35m\u00b2, with no dimension less than 380mm. The bottom of the opening must be no more than 1000mm above the floor. Per NBC 9.9.10.1.",
  },
  {
    question:
      "What is the minimum ceiling height for a habitable room in Alberta?",
    answer:
      "2.3m (7 feet 6.5 inches) per NBC(AE) 2023 Section 9.5.3.1 for at least 50% of the required floor area.",
  },
  {
    question: "What fire separation is required between a garage and house?",
    answer:
      "45-minute fire resistance rating for the wall between an attached garage and dwelling unit, per NBC 9.10.9.18.",
  },
  {
    question: "Do I need a permit for basement development in Calgary?",
    answer:
      "Yes, Calgary requires a building permit for basement developments. This includes electrical, plumbing, and HVAC permits if those systems are being modified.",
  },
];

export const PERMITS_PAGE_FAQS = [
  {
    question: "How much does a building permit cost in Calgary?",
    answer:
      "Calgary building permit fees are calculated based on project value. For residential projects, fees typically range from $200-$500 for minor renovations to $2,000-$5,000+ for new construction.",
  },
  {
    question: "How long does a Calgary building permit take?",
    answer:
      "Simple residential permits (decks, fences) take 2-4 weeks. Complex residential (basement development, additions) take 4-8 weeks. Commercial permits vary from 4-16 weeks depending on complexity.",
  },
  {
    question:
      "What is the difference between a development permit and building permit in Calgary?",
    answer:
      "A development permit (DP) approves the use and placement of a building on the land. A building permit (BP) approves the construction details. Many projects require both.",
  },
  {
    question: "Do I need a permit for a secondary suite in Calgary?",
    answer:
      "Yes, secondary suites in Calgary require both a development permit (to verify zoning compliance) and a building permit (to verify code compliance).",
  },
];

export const QUANTITY_SURVEY_FAQS = [
  {
    question: "How much does it cost to build a house in Calgary in 2026?",
    answer:
      "In 2026, Calgary construction costs range from $200-280/sq ft for economy builds, $280-380/sq ft for standard builds, $380-500/sq ft for custom/premium homes, and $500-800+/sq ft for luxury builds. A typical 2,200 sq ft Calgary home costs $600,000-$900,000 to build (excluding land).",
  },
  {
    question:
      "What is the average construction cost per square foot in Alberta?",
    answer:
      "Alberta construction costs vary by region: Calgary $280-380/sq ft, Edmonton $260-360/sq ft, Red Deer $250-340/sq ft, and Rural Alberta $240-320/sq ft. These rates include climate requirements like R-24 wall insulation and frost-protected foundations.",
  },
  {
    question: "What is the average cost of basement development in Calgary?",
    answer:
      "Basement development in Calgary typically costs $50-$100/sq ft, averaging $40,000-$80,000 for a 800 sq ft basement. This includes framing, electrical, plumbing, HVAC, and basic finishes.",
  },
  {
    question: "What is a quantity survey in construction?",
    answer:
      "A quantity survey is a professional measurement and costing of all materials, labour, and equipment needed for a construction project. It follows CIQS (Canadian Institute of Quantity Surveyors) methodology and forms the basis of accurate budget estimation.",
  },
  {
    question: "Do I need a quantity surveyor for my construction project?",
    answer:
      "For small renovations (<$50K), DIY estimates suffice. Medium projects ($50K-$500K) can use free online tools. Large projects (>$500K) and commercial/multi-unit developments should hire a professional QS. Professional quantity surveyors in Calgary charge $2,000-$10,000.",
  },
  {
    question: "What affects construction costs in Calgary?",
    answer:
      "Calgary construction costs are affected by climate requirements (R-24+ insulation, frost foundations), labour market rates ($55-85/hr for trades), material prices (US tariffs impact lumber and steel), permit fees (1.014% of construction value), and location (inner-city infills cost 15-25% more).",
  },
  {
    question: "What is included in a Calgary construction cost estimate?",
    answer:
      "A comprehensive estimate includes hard costs (70-75%): site prep, foundation, framing, roofing, HVAC, plumbing, electrical, finishes. Plus soft costs (25-30%): architectural fees (6-12%), permits (2%), contingency (10-15%), project management. Land costs are NOT included.",
  },
  {
    question: "How accurate are online construction cost calculators?",
    answer:
      "Online parametric estimators provide +/- 15-25% accuracy for feasibility. With detailed inputs, accuracy improves to +/- 10-15%. Professional QS estimates achieve +/- 5-10%. Our calculator uses 2026 Calgary market data from CIQS Alberta, NRCan, and Statistics Canada.",
  },
  {
    question:
      "Is Builder Buddy free for Calgary Construction Association (CCA) members?",
    answer:
      "Yes, Builder Buddy is 100% free for all users, including CCA and BILD Calgary Region members. Our tools — quantity surveys, feasibility studies, code lookups, and permit checking — require no signup or payment.",
  },
];

export const FEASIBILITY_STUDY_FAQS = [
  {
    question: "What is a construction feasibility study?",
    answer:
      "A construction feasibility study analyzes the viability of a building project by examining costs, timelines, material availability, regulatory requirements, and market conditions. Essential for developers and investors in Calgary.",
  },
  {
    question: "How do tariffs affect construction costs in Alberta?",
    answer:
      "US tariffs on Canadian goods and Canadian retaliatory tariffs impact construction material costs. Steel, aluminum, and lumber are particularly affected. Our calculator helps compare Canadian vs US suppliers to optimize costs.",
  },
  {
    question:
      "What government incentives are available for construction in Alberta?",
    answer:
      "Alberta offers various incentives including energy efficiency rebates, affordable housing grants, and municipal programs. Calgary has specific programs for infill development, secondary suites, and green building.",
  },
  {
    question:
      "Can developers use Builder Buddy for investment analysis in Calgary?",
    answer:
      "Yes. Our feasibility study tool helps developers and small investors analyze project ROI with real Calgary land costs, construction estimates, government incentives, and material price comparisons. Ideal for infill, multi-family, and secondary suite developments.",
  },
];

/**
 * Service schema for professional services
 * Used to highlight specific service offerings
 */
export function getServiceSchema(
  serviceName: string,
  serviceDescription: string,
  serviceUrl: string,
  serviceType: string = "ProfessionalService",
) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType,
    name: serviceName,
    description: serviceDescription,
    url: serviceUrl,
    provider: {
      "@type": "Organization",
      name: BUSINESS_INFO.name,
      url: SITE_URL,
    },
    areaServed: [
      {
        "@type": "City",
        name: "Calgary",
        containedInPlace: {
          "@type": "AdministrativeArea",
          name: "Alberta",
        },
      },
      {
        "@type": "AdministrativeArea",
        name: "Alberta",
        containedInPlace: {
          "@type": "Country",
          name: "Canada",
        },
      },
    ],
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "CAD",
      availability: "https://schema.org/InStock",
    },
    audience: {
      "@type": "Audience",
      audienceType:
        "Builders, general contractors, construction managers, real estate developers, small investors, architects, permit expediters, project managers, cost consultants, quantity surveyors, CCA members, BILD Calgary Region members, homeowners",
    },
  };
}

/**
 * ProfessionalService schema for quantity surveying
 */
export function getQuantitySurveyServiceSchema() {
  return getServiceSchema(
    "Quantity Survey & Construction Cost Estimator Calgary",
    "Free online quantity survey and construction cost estimator for Calgary and Alberta. Professional-grade calculator for building costs per square foot, material quantities, bill of quantities (BOQ), and labour estimates using CIQS methodology with Calgary location factors.",
    `${SITE_URL}/quantity-survey`,
    "QuantitySurveyingService",
  );
}

/**
 * ProfessionalService schema for feasibility studies
 */
export function getFeasibilityStudyServiceSchema() {
  return getServiceSchema(
    "Construction Feasibility Study Tool",
    "Analyze construction material sourcing, compare Canadian vs US suppliers, calculate tariff impacts, and find government incentives for Calgary and Alberta construction projects.",
    `${SITE_URL}/feasibility`,
    "ConsultingService",
  );
}

/**
 * ProfessionalService schema for building code consulting
 */
export function getBuildingCodeConsultingSchema() {
  return getServiceSchema(
    "AI Building Code Consultant",
    "AI-powered building code assistance for Calgary and Alberta. Get instant answers about NBC 2023 Alberta Edition, building permits, zoning bylaws, and construction requirements.",
    `${SITE_URL}/chat`,
    "ConsultingService",
  );
}

/**
 * LocalBusiness schema with enhanced Calgary targeting
 */
export function getEnhancedLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#localbusiness`,
    name: BUSINESS_INFO.name,
    description:
      "AI-powered building code assistant and construction cost estimator serving Calgary's construction industry. Free tools for builders, general contractors, developers, investors, architects, permit expediters, and consultants. Trusted by CCA and BILD Calgary Region professionals.",
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.svg`,
    image: `${SITE_URL}/og-image.png`,
    telephone: BUSINESS_INFO.telephone,
    priceRange: "Free",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Calgary",
      addressRegion: "Alberta",
      addressCountry: "CA",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 51.0447,
      longitude: -114.0719,
    },
    areaServed: [
      {
        "@type": "City",
        name: "Calgary",
      },
      {
        "@type": "City",
        name: "Airdrie",
      },
      {
        "@type": "City",
        name: "Cochrane",
      },
      {
        "@type": "City",
        name: "Okotoks",
      },
      {
        "@type": "City",
        name: "Chestermere",
      },
      {
        "@type": "AdministrativeArea",
        name: "Rocky View County",
      },
      {
        "@type": "AdministrativeArea",
        name: "Alberta",
      },
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Construction & Building Code Services",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Construction Cost Estimator",
            description:
              "Free construction cost calculator for Calgary projects",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Building Code AI Assistant",
            description: "AI-powered answers to building code questions",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Permit Requirements Checker",
            description: "Check Calgary building permit requirements and costs",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "SDAB Appeal Risk Assessment",
            description: "Development appeal risk analysis for Calgary",
          },
        },
      ],
    },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ],
      opens: "00:00",
      closes: "23:59",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      reviewCount: "127",
      bestRating: "5",
      worstRating: "1",
    },
  };
}

/**
 * Keywords for Calgary construction SEO
 */
export const CALGARY_CONSTRUCTION_KEYWORDS = [
  "Calgary construction costs",
  "Alberta building permits",
  "quantity surveyor Calgary",
  "construction cost estimator Alberta",
  "feasibility study Calgary",
  "Calgary building code consultant",
  "NBC 2023 Alberta",
  "Calgary contractor cost calculator",
  "building cost per square foot Calgary",
  "basement development cost Calgary",
  "Calgary infill construction",
  "Alberta construction estimating",
  "Calgary development permit",
  "SDAB Calgary",
  "Calgary zoning bylaw",
  "Calgary builder tools",
  "general contractor Calgary",
  "construction manager tools Calgary",
  "permit expediter Calgary",
  "CCA Calgary Construction Association",
  "BILD Calgary Region",
  "Calgary developer tools",
  "small investor Calgary construction",
  "repeat permits Calgary",
  "cost consultant Calgary",
  "project manager Calgary construction",
];
